import { useEffect, useState } from "react";
import api from "../api";
import { useI18n } from "../i18n";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge, eur, fmtDate, CHANNELS, PAY_METHODS, PAY_STATUS, DOC_STATUS, CHK_STATUS } from "../helpers";
import { Field } from "../components/FormField";
import ImageUpload, { fullUrl } from "../components/ImageUpload";
import SignaturePad from "../components/SignaturePad";
import { Plus, Pencil, Trash2, UserCheck, FileCheck, X, ClipboardList, Route } from "lucide-react";
import BookingDetailDialog from "../components/BookingDetailDialog";

const today = new Date().toISOString().slice(0, 10);
const empty = {
  apartment_id: "", guest_name: "", guest_email: "", guest_phone: "", guests: 1, check_in: today, check_out: today,
  channel: "diretto", total_amount: 0, amount_nightly: 0, amount_cleaning: 0, amount_extra: 0, city_tax: 0, paid_amount: 0,
  ota_commission_override: "", stripe_fee_override: "", payment_method: "OTA", payment_status: "in_sospeso",
  document_status: "mancante", checkin_instructions_status: "da_inviare", notes: "", booking_reference: "",
  internal_tasks: [], registered_guests: [], documents: [], agreement_url: "", agreement_signed: false, checkin_completed: false,
};
const emptyGuest = { name: "", birth_date: "", doc_type: "Carta d'identità", doc_number: "", doc_url: "" };
const uid = () => Math.random().toString(36).slice(2);

export default function Bookings() {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [apts, setApts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [newTask, setNewTask] = useState("");
  // check-in
  const [ciOpen, setCiOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [ci, setCi] = useState(null);
  const [guest, setGuest] = useState(emptyGuest);
  const [sigSaving, setSigSaving] = useState(false);

  const load = () => api.get("/bookings").then((r) => setItems(r.data));
  useEffect(() => { load(); api.get("/apartments/lookup").then((r) => setApts(r.data)); }, []);

  const aptName = (id) => apts.find((a) => a.id === id)?.name || "—";
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openNew = () => { setForm({ ...empty, apartment_id: apts[0]?.id || "" }); setOpen(true); };
  const openEdit = (it) => { setForm({ ...empty, ...it, ota_commission_override: it.ota_commission_override ?? "", stripe_fee_override: it.stripe_fee_override ?? "" }); setOpen(true); };

  const save = async () => {
    if (!form.apartment_id || !form.guest_name.trim()) { toast.error(t("guestName")); return; }
    const payload = {
      ...form,
      ota_commission_override: form.ota_commission_override === "" || form.ota_commission_override === null ? null : +form.ota_commission_override,
      stripe_fee_override: form.stripe_fee_override === "" || form.stripe_fee_override === null ? null : +form.stripe_fee_override,
    };
    try {
      if (form.id) await api.put(`/bookings/${form.id}`, payload);
      else await api.post("/bookings", payload);
      toast.success(t("saved")); setOpen(false); load();
    } catch { toast.error("Error"); }
  };
  const del = async (id) => { if (!window.confirm(t("confirm"))) return; await api.delete(`/bookings/${id}`); toast.success(t("deleted")); load(); };
  const optLabel = (s) => t({ pagato: "paid", parziale: "partial", in_sospeso: "pending", ricevuto: "received", mancante: "missing", inviate: "sent", da_inviare: "toSend" }[s] || s);

  // internal tasks
  const addTask = () => { if (!newTask.trim()) return; set("internal_tasks", [...(form.internal_tasks || []), { id: uid(), text: newTask, done: false }]); setNewTask(""); };
  const toggleTask = (id) => set("internal_tasks", form.internal_tasks.map((x) => x.id === id ? { ...x, done: !x.done } : x));
  const removeTask = (id) => set("internal_tasks", form.internal_tasks.filter((x) => x.id !== id));

  // ---- check-in
  const openCheckin = (b) => { setCi({ ...empty, ...b }); setGuest(emptyGuest); setCiOpen(true); };
  const ciSet = (k, v) => setCi((f) => ({ ...f, [k]: v }));
  const addGuest = () => {
    if (!guest.name.trim()) { toast.error(t("guestName")); return; }
    ciSet("registered_guests", [...(ci.registered_guests || []), { ...guest, id: uid() }]);
    setGuest(emptyGuest);
  };
  const removeGuest = (id) => ciSet("registered_guests", ci.registered_guests.filter((g) => g.id !== id));

  const uploadBlob = async (blob, name) => {
    const fd = new FormData(); fd.append("file", new File([blob], name, { type: blob.type || "image/png" }));
    const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return data.url;
  };
  const saveSignature = async (blob) => {
    setSigSaving(true);
    try { const url = await uploadBlob(blob, "agreement-signature.png"); ciSet("agreement_url", url); ciSet("agreement_signed", true); toast.success(t("saved")); }
    catch { toast.error("Error"); } finally { setSigSaving(false); }
  };
  const addDocument = (url) => { if (url) ciSet("documents", [...(ci.documents || []), { id: uid(), name: `Doc ${(ci.documents || []).length + 1}`, url, uploaded_at: new Date().toISOString() }]); };

  const saveCheckin = async (complete) => {
    const payload = { ...ci };
    if (complete) { payload.checkin_completed = true; if (payload.document_status === "mancante") payload.document_status = "ricevuto"; payload.checkin_instructions_status = "inviate"; }
    try {
      await api.put(`/bookings/${ci.id}`, payload);
      toast.success(t("saved")); setCiOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <div className="space-y-6 fade-in" data-testid="bookings-page">
      <div className="flex justify-end">
        <Button data-testid="add-booking-btn" onClick={openNew} className="gap-2 bg-primary hover:bg-primary/90"><Plus size={18} /> {t("add")}</Button>
      </div>

      <Card className="overflow-hidden border-slate-200">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-3">{t("guestName")}</th>
                <th className="px-4 py-3">{t("apartment")}</th>
                <th className="px-4 py-3">{t("checkIn")}</th>
                <th className="px-4 py-3">{t("checkOut")}</th>
                <th className="px-4 py-3">{t("channel")}</th>
                <th className="px-4 py-3">{t("totalAmount")}</th>
                <th className="px-4 py-3">{t("paymentStatus")}</th>
                <th className="px-4 py-3">{t("checkin")}</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((b) => (
                <tr key={b.id} data-testid={`booking-row-${b.id}`} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3 font-medium text-slate-900">{b.guest_name}<span className="ml-1 text-xs text-slate-400">· {b.guests}</span></td>
                  <td className="px-4 py-3 text-slate-600">{aptName(b.apartment_id)}</td>
                  <td className="px-4 py-3 text-slate-600">{fmtDate(b.check_in)}</td>
                  <td className="px-4 py-3 text-slate-600">{fmtDate(b.check_out)}</td>
                  <td className="px-4 py-3 capitalize text-slate-600">{b.channel}</td>
                  <td className="px-4 py-3 font-medium text-slate-900">{eur(b.total_amount)}</td>
                  <td className="px-4 py-3"><StatusBadge value={b.payment_status} /></td>
                  <td className="px-4 py-3">
                    {b.checkin_completed
                      ? <span data-testid={`checkin-done-${b.id}`} className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-700"><FileCheck size={12} /> OK</span>
                      : <span className="text-xs text-slate-400">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1">
                      <Button data-testid={`detail-booking-${b.id}`} variant="ghost" size="icon" title="Scheda & Customer Journey" onClick={() => setDetailId(b.id)}><Route size={15} className="text-indigo-600" /></Button>
                      <Button data-testid={`checkin-booking-${b.id}`} variant="ghost" size="icon" title={t("checkinDigital")} onClick={() => openCheckin(b)}><UserCheck size={15} className="text-primary" /></Button>
                      <Button data-testid={`edit-booking-${b.id}`} variant="ghost" size="icon" onClick={() => openEdit(b)}><Pencil size={15} /></Button>
                      <Button data-testid={`delete-booking-${b.id}`} variant="ghost" size="icon" className="text-rose-600" onClick={() => del(b.id)}><Trash2 size={15} /></Button>
                    </div>
                  </td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={9} className="px-4 py-8 text-center text-slate-400">{t("noData")}</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      {detailId && (
        <BookingDetailDialog open={!!detailId} onOpenChange={(o) => { if (!o) { setDetailId(null); load(); } }} bookingId={detailId} />
      )}

      {/* Reservation edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader><DialogTitle>{form.id ? t("edit") : t("add")} · {t("bookings")}</DialogTitle><DialogDescription className="sr-only">Form</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t("apartment")} className="col-span-2">
              <Select value={form.apartment_id} onValueChange={(v) => set("apartment_id", v)}>
                <SelectTrigger data-testid="booking-apartment"><SelectValue placeholder={t("selectApartment")} /></SelectTrigger>
                <SelectContent>{apts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label={t("guestName")}><Input data-testid="booking-guest" value={form.guest_name} onChange={(e) => set("guest_name", e.target.value)} /></Field>
            <Field label={t("guests")}><Input type="number" value={form.guests} onChange={(e) => set("guests", +e.target.value)} /></Field>
            <Field label={t("email")}><Input value={form.guest_email} onChange={(e) => set("guest_email", e.target.value)} /></Field>
            <Field label={t("phone")}><Input value={form.guest_phone} onChange={(e) => set("guest_phone", e.target.value)} /></Field>
            <Field label={t("checkIn")}><Input data-testid="booking-checkin" type="date" value={form.check_in} onChange={(e) => set("check_in", e.target.value)} /></Field>
            <Field label={t("checkOut")}><Input data-testid="booking-checkout" type="date" value={form.check_out} onChange={(e) => set("check_out", e.target.value)} /></Field>
            <Field label={t("channel")}>
              <Select value={form.channel} onValueChange={(v) => set("channel", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{CHANNELS.map((c) => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label={t("bookingReference")}><Input data-testid="booking-reference" value={form.booking_reference} onChange={(e) => set("booking_reference", e.target.value)} /></Field>
            <Field label={t("paymentMethod")}>
              <Select value={form.payment_method} onValueChange={(v) => set("payment_method", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{PAY_METHODS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label={t("totalAmount")}><Input type="number" value={form.total_amount} onChange={(e) => set("total_amount", +e.target.value)} /></Field>
            <Field label={t("cityTax")}><Input type="number" value={form.city_tax} onChange={(e) => set("city_tax", +e.target.value)} /></Field>
            <div className="col-span-2 -mb-1 mt-1 text-xs font-medium text-slate-500">{t("revenueBreakdown")}</div>
            <Field label={t("nightly")}><Input data-testid="booking-nightly" type="number" value={form.amount_nightly} onChange={(e) => set("amount_nightly", +e.target.value)} /></Field>
            <Field label={t("cleaningRev")}><Input data-testid="booking-cleaning-rev" type="number" value={form.amount_cleaning} onChange={(e) => set("amount_cleaning", +e.target.value)} /></Field>
            <Field label={t("extraRev")}><Input type="number" value={form.amount_extra} onChange={(e) => set("amount_extra", +e.target.value)} /></Field>
            <Field label={t("otaOverride")}><Input data-testid="booking-ota-override" type="number" placeholder="auto" value={form.ota_commission_override} onChange={(e) => set("ota_commission_override", e.target.value)} /></Field>
            <Field label={t("paidAmount")}><Input type="number" value={form.paid_amount} onChange={(e) => set("paid_amount", +e.target.value)} /></Field>
            <Field label={t("paymentStatus")}>
              <Select value={form.payment_status} onValueChange={(v) => set("payment_status", v)}>
                <SelectTrigger data-testid="booking-paystatus"><SelectValue /></SelectTrigger>
                <SelectContent>{PAY_STATUS.map((c) => <SelectItem key={c} value={c}>{optLabel(c)}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label={t("docStatus")}>
              <Select value={form.document_status} onValueChange={(v) => set("document_status", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{DOC_STATUS.map((c) => <SelectItem key={c} value={c}>{optLabel(c)}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label={t("checkinStatus")}>
              <Select value={form.checkin_instructions_status} onValueChange={(v) => set("checkin_instructions_status", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{CHK_STATUS.map((c) => <SelectItem key={c} value={c}>{optLabel(c)}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label={t("notes")} className="col-span-2"><Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} /></Field>
            {/* internal tasks */}
            <div className="col-span-2">
              <div className="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-slate-600"><ClipboardList size={14} /> {t("internalTasks")}</div>
              <div className="space-y-2">
                {(form.internal_tasks || []).map((task) => (
                  <div key={task.id} className="flex items-center gap-2 rounded-md bg-slate-50 px-3 py-2">
                    <Checkbox checked={task.done} onCheckedChange={() => toggleTask(task.id)} />
                    <span className={`flex-1 text-sm ${task.done ? "text-slate-400 line-through" : "text-slate-700"}`}>{task.text}</span>
                    <button type="button" onClick={() => removeTask(task.id)} className="text-slate-400 hover:text-rose-600"><Trash2 size={14} /></button>
                  </div>
                ))}
                <div className="flex gap-2">
                  <Input value={newTask} data-testid="booking-newtask" onChange={(e) => setNewTask(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addTask())} placeholder={t("addTask")} />
                  <Button type="button" variant="outline" onClick={addTask}>+</Button>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t("cancel")}</Button>
            <Button data-testid="save-booking-btn" onClick={save} className="bg-primary hover:bg-primary/90">{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Digital check-in dialog */}
      <Dialog open={ciOpen} onOpenChange={setCiOpen}>
        <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-xl" data-testid="checkin-dialog">
          <DialogHeader>
            <DialogTitle>{t("checkinDigital")} · {ci?.guest_name}</DialogTitle>
            <DialogDescription>{ci ? `${aptName(ci.apartment_id)} · ${fmtDate(ci.check_in)} → ${fmtDate(ci.check_out)}` : ""}</DialogDescription>
          </DialogHeader>
          {ci && (
            <div className="space-y-5">
              {/* registered guests */}
              <div>
                <h4 className="mb-2 font-head text-sm font-semibold text-slate-900">{t("registeredGuests")} ({(ci.registered_guests || []).length})</h4>
                <div className="space-y-2">
                  {(ci.registered_guests || []).map((g) => (
                    <div key={g.id} className="flex items-center gap-3 rounded-md border border-border p-2" data-testid={`reg-guest-${g.id}`}>
                      {g.doc_url && <img src={fullUrl(g.doc_url)} alt="" className="h-10 w-10 rounded object-cover" />}
                      <div className="flex-1 text-sm">
                        <div className="font-medium text-slate-800">{g.name}</div>
                        <div className="text-xs text-slate-500">{g.doc_type} {g.doc_number} {g.birth_date && `· ${g.birth_date}`}</div>
                      </div>
                      <button type="button" onClick={() => removeGuest(g.id)} className="text-slate-400 hover:text-rose-600"><X size={15} /></button>
                    </div>
                  ))}
                </div>
                <div className="mt-2 grid grid-cols-2 gap-2 rounded-lg bg-secondary/40 p-3">
                  <Input data-testid="reg-guest-name" placeholder={t("guestName")} value={guest.name} onChange={(e) => setGuest((g) => ({ ...g, name: e.target.value }))} />
                  <Input type="date" placeholder={t("birthDate")} value={guest.birth_date} onChange={(e) => setGuest((g) => ({ ...g, birth_date: e.target.value }))} />
                  <Input placeholder={t("docType")} value={guest.doc_type} onChange={(e) => setGuest((g) => ({ ...g, doc_type: e.target.value }))} />
                  <Input data-testid="reg-guest-docnum" placeholder={t("docNumber")} value={guest.doc_number} onChange={(e) => setGuest((g) => ({ ...g, doc_number: e.target.value }))} />
                  <div className="col-span-2"><ImageUpload label={t("idDocument")} value={guest.doc_url} onChange={(u) => setGuest((g) => ({ ...g, doc_url: u }))} testid="reg-guest-iddoc" /></div>
                  <Button type="button" data-testid="add-reg-guest-btn" variant="outline" className="col-span-2 gap-1" onClick={addGuest}><Plus size={14} /> {t("addGuest")}</Button>
                </div>
              </div>

              {/* rental agreement signature */}
              <div>
                <h4 className="mb-2 font-head text-sm font-semibold text-slate-900">{t("rentalAgreement")} — {t("signature")}</h4>
                <SignaturePad onSave={saveSignature} saving={sigSaving} savedUrl={ci.agreement_url} />
                {ci.agreement_url && <a href={fullUrl(ci.agreement_url)} target="_blank" rel="noreferrer" className="mt-1 inline-block text-xs text-primary hover:underline">{t("signature")} →</a>}
              </div>

              {/* additional documents */}
              <div>
                <h4 className="mb-2 font-head text-sm font-semibold text-slate-900">{t("documents")} ({(ci.documents || []).length})</h4>
                <div className="mb-2 flex flex-wrap gap-2">
                  {(ci.documents || []).map((d) => (
                    <a key={d.id} href={fullUrl(d.url)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs text-slate-600 hover:bg-slate-50"><FileCheck size={12} /> {d.name}</a>
                  ))}
                </div>
                <div className="max-w-[220px]"><ImageUpload label={t("uploadDoc")} value="" onChange={addDocument} testid="checkin-doc-upload" /></div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" data-testid="save-checkin-btn" onClick={() => saveCheckin(false)}>{t("save")}</Button>
            <Button data-testid="complete-checkin-btn" onClick={() => saveCheckin(true)} className="gap-1 bg-primary hover:bg-primary/90"><FileCheck size={16} /> {t("completeCheckin")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
